import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, X, FileText } from 'lucide-react';
import { searchContracts } from '../features/contracts/contractLookupApi';

/**
 * ContractPicker — typeahead for selecting another contract.
 *
 * Used by the Parent Contract field on ContractForm (when an Amendment needs
 * to reference the master agreement).
 *
 * Props:
 *   value        — current selected contract object { id, title, counterparty, shortName, contractType } or null
 *   onChange     — fn(contractOrNull) — called with the selected object or null to clear
 *   excludeId    — a contract ID to exclude from results (used to prevent self-select when editing)
 *   label        — field label
 *   placeholder  — search placeholder
 */
export default function ContractPicker({
  value,
  onChange,
  excludeId,
  label = 'Contract',
  placeholder = 'Search by title or counterparty...'
}) {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const menuRef = useRef(null);
  const debounceRef = useRef(null);
  // Viewport coords for the results menu. Recomputed on open and on
  // scroll/resize so position:fixed keeps the menu glued to the input.
  // Needed because cards set overflow:clip, which was cutting the
  // absolute-positioned menu off at the card boundary.
  const [menuPos, setMenuPos] = useState(null);

  const updateMenuPos = useCallback(() => {
    if (!inputRef.current) return;
    const rect = inputRef.current.getBoundingClientRect();
    const gap = 4;
    const below = window.innerHeight - rect.bottom;
    const above = rect.top;
    const maxHeight = 260;
    const openUp = below < maxHeight && above > below;
    setMenuPos({
      left: rect.left,
      top: openUp ? rect.top - gap : rect.bottom + gap,
      width: rect.width,
      maxHeight: Math.min(maxHeight, Math.max(120, openUp ? above - gap - 8 : below - gap - 8)),
      openUp,
    });
  }, []);

  // Close on outside click; keep the menu anchored to the input while open.
  useEffect(() => {
    const handler = (e) => {
      const inWrapper = wrapperRef.current && wrapperRef.current.contains(e.target);
      const inMenu = menuRef.current && menuRef.current.contains(e.target);
      if (!inWrapper && !inMenu) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    updateMenuPos();
    const onScrollOrResize = () => updateMenuPos();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open, updateMenuPos]);

  // Debounced search
  useEffect(() => {
    if (!open) return;
    if (!query.trim()) {
      setSuggestions([]);
      return;
    }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setLoading(true);
      searchContracts({ q: query, limit: 10, excludeId })
        .then(data => {
          setSuggestions(data?.contracts || []);
          setLoading(false);
        })
        .catch(() => {
          setSuggestions([]);
          setLoading(false);
        });
    }, 200);
    return () => clearTimeout(debounceRef.current);
  }, [query, open, excludeId]);

  if (value) {
    // Display mode — show the selected contract as a pill with a clear button
    return (
      <div>
        {label && (
          <label className="field-label">
            {label}
          </label>
        )}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            minHeight: 42,
            padding: '9px 10px',
            border: 'var(--stroke-xs) solid var(--border-primary)',
            borderRadius: 8,
            background: 'var(--surface-panel)',
          }}
        >
          <FileText className="w-4 h-4" style={{ color: 'var(--icon-tertiary)', flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <p
              className="ellipsis"
              style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)', margin: 0 }}
            >
              {value.title}
            </p>
            {(value.counterparty || value.contractType) && (
              <p
                className="ellipsis"
                style={{ fontSize: 11, color: 'var(--text-secondary)', margin: '2px 0 0' }}
              >
                {[value.contractType, value.counterparty].filter(Boolean).join(' • ')}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={() => onChange(null)}
            className="icon-btn xs"
            title="Clear"
            aria-label="Clear selected contract"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      {label && (
        <label className="field-label">
          {label}
        </label>
      )}
      <div style={{ position: 'relative' }}>
        <Search
          className="w-4 h-4"
          style={{
            position: 'absolute',
            left: 11,
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'var(--icon-tertiary)',
            pointerEvents: 'none',
          }}
        />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          className="input"
          style={{ width: '100%', paddingLeft: 34 }}
        />
      </div>
      {open && query.trim() && menuPos && (
        <div
          ref={menuRef}
          style={{
            position: 'fixed',
            top: menuPos.openUp ? 'auto' : menuPos.top,
            bottom: menuPos.openUp ? (window.innerHeight - menuPos.top) : 'auto',
            left: menuPos.left,
            width: menuPos.width,
            zIndex: 1000,
            maxHeight: menuPos.maxHeight,
            overflowY: 'auto',
            background: 'var(--surface-base)',
            border: 'var(--stroke-xs) solid var(--border-primary)',
            borderRadius: 8,
            boxShadow: 'var(--shadow-lg)',
          }}
        >
          {loading ? (
            <p style={{ padding: 12, fontSize: 13, color: 'var(--text-tertiary)', margin: 0 }}>Searching...</p>
          ) : suggestions.length === 0 ? (
            <p style={{ padding: 12, fontSize: 13, color: 'var(--text-tertiary)', margin: 0 }}>No contracts match.</p>
          ) : (
            suggestions.map((c, index) => (
              <button
                key={c.id}
                type="button"
                onClick={() => {
                  onChange(c);
                  setQuery('');
                  setOpen(false);
                }}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '9px 12px',
                  textAlign: 'left',
                  background: 'transparent',
                  border: 0,
                  borderBottom: index === suggestions.length - 1
                    ? 0
                    : 'var(--stroke-xs) solid var(--border-primary)',
                  cursor: 'pointer',
                  font: 'inherit',
                }}
              >
                <p
                  className="ellipsis"
                  style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)', margin: 0 }}
                >
                  {c.title}
                </p>
                <p
                  className="ellipsis"
                  style={{ fontSize: 11, color: 'var(--text-secondary)', margin: '2px 0 0' }}
                >
                  {[c.contractType, c.counterparty].filter(Boolean).join(' • ') || '—'}
                </p>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
